import React, { useEffect, useState } from 'react';
import { AlertTriangle, Save, CheckCircle } from 'lucide-react';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useQueryClient } from '@tanstack/react-query';
import ErrorMessage from '../ErrorMessage';

type RiskLevel = 'low' | 'mid' | 'high';

interface AlertSeverityConfig {
  severity_sanctioned: RiskLevel;
  severity_pep: RiskLevel;
  severity_adverse_media: RiskLevel;
  severity_other: RiskLevel;
}

const AlertRiskLevelsSettings: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.id || '';
  const queryClient = useQueryClient();

  const [severityConfig, setSeverityConfig] = useState<AlertSeverityConfig>({
    severity_sanctioned: 'high',
    severity_pep: 'mid',
    severity_adverse_media: 'mid',
    severity_other: 'mid',
  });

  // Fetch current alert severity configuration
  const severityQuery = $api.useQuery(
    'get',
    '/api/organizations/{organization_id}/alert-severity',
    {
      params: {
        path: {
          organization_id: organizationId,
        },
      },
    },
    {
      enabled: !!organizationId,
    }
  );

  useEffect(() => {
    if (severityQuery.data) {
      setSeverityConfig(severityQuery.data);
    }
  }, [severityQuery.data]);

  // Update alert severity configuration
  const updateSeverityMutation = $api.useMutation(
    'put',
    '/api/organizations/{organization_id}/alert-severity'
  );

  const handleSeverityChange = (key: keyof AlertSeverityConfig, value: RiskLevel) => {
    setSeverityConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    updateSeverityMutation.mutate(
      {
        params: {
          path: {
            organization_id: organizationId,
          },
        },
        body: severityConfig,
      },
      {
        onSuccess: () => {
          // Invalidate and refetch to ensure UI is in sync
          queryClient.invalidateQueries({
            queryKey: ['get', '/api/organizations/{organization_id}/alert-severity'],
          });
          severityQuery.refetch();
        },
      }
    );
  };

  const riskLevelLabels: Record<RiskLevel, { label: string; description: string; color: string }> = {
    low: {
      label: 'Faible',
      description: 'Alerts de faible priorité, notifications minimales',
      color: 'green',
    },
    mid: {
      label: 'Moyen',
      description: 'Alerts de priorité moyenne, notifications standard',
      color: 'yellow',
    },
    high: {
      label: 'Élevé',
      description: 'Alerts critiques, notifications immédiates',
      color: 'red',
    },
  };

  const alertTypes: Array<{
    key: keyof AlertSeverityConfig;
    label: string;
    description: string;
  }> = [
    {
      key: 'severity_sanctioned',
      label: 'Sanctions',
      description: 'Alertes pour les entités sanctionnées ou sur liste noire',
    },
    {
      key: 'severity_pep',
      label: 'PEP (Personnes Politiquement Exposées)',
      description: 'Alertes pour les personnes politiquement exposées',
    },
    {
      key: 'severity_adverse_media',
      label: 'Médias défavorables',
      description: 'Alertes basées sur des informations négatives dans les médias',
    },
    {
      key: 'severity_other',
      label: 'Autres',
      description: 'Autres types d\'alertes et risques identifiés',
    },
  ];

  if (!currentOrganization) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500 py-12">
          <p>Aucune organisation sélectionnée</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <AlertTriangle className="h-6 w-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Niveaux de risque des alertes</h3>
        </div>
        <p className="text-sm text-gray-600">
          Configurez le niveau de priorité pour chaque type d'alerte de l'organisation{' '}
          <span className="font-medium">{currentOrganization.name}</span>
        </p>
      </div>

      {/* Error Messages */}
      {(severityQuery.error || updateSeverityMutation.error) && (
        <ErrorMessage
          error={severityQuery.error || updateSeverityMutation.error}
          className="mb-6"
        />
      )}

      {/* Success Message */}
      {updateSeverityMutation.isSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800 text-sm">
            Configuration des niveaux de risque sauvegardée avec succès
          </p>
        </div>
      )}

      {severityQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement de la configuration...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {alertTypes.map((alertType) => {
              const currentLevel = severityConfig[alertType.key];

              return (
                <div
                  key={alertType.key}
                  className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-1">{alertType.label}</h4>
                    <p className="text-sm text-gray-500">{alertType.description}</p>
                  </div>

                  <div className="flex items-center space-x-4">
                    {(['low', 'mid', 'high'] as RiskLevel[]).map((level) => {
                      const info = riskLevelLabels[level];
                      const isSelected = currentLevel === level;
                      
                      // Get color classes based on level
                      const colorClasses = {
                        low: isSelected
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300',
                        mid: isSelected
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-gray-200 hover:border-gray-300',
                        high: isSelected
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300',
                      };
                      
                      const dotColorClasses = {
                        low: isSelected ? 'bg-green-600' : 'bg-gray-300',
                        mid: isSelected ? 'bg-yellow-600' : 'bg-gray-300',
                        high: isSelected ? 'bg-red-600' : 'bg-gray-300',
                      };
                      
                      const textColorClasses = {
                        low: isSelected ? 'text-green-900' : 'text-gray-600',
                        mid: isSelected ? 'text-yellow-900' : 'text-gray-600',
                        high: isSelected ? 'text-red-900' : 'text-gray-600',
                      };
                      
                      const descColorClasses = {
                        low: isSelected ? 'text-green-700' : 'text-gray-500',
                        mid: isSelected ? 'text-yellow-700' : 'text-gray-500',
                        high: isSelected ? 'text-red-700' : 'text-gray-500',
                      };

                      return (
                        <label
                          key={level}
                          className={`flex-1 p-3 border-2 rounded-lg cursor-pointer transition-all ${colorClasses[level]}`}
                        >
                          <input
                            type="radio"
                            name={alertType.key}
                            value={level}
                            checked={isSelected}
                            onChange={() => handleSeverityChange(alertType.key, level)}
                            className="sr-only"
                          />
                          <div className="flex items-center space-x-2 mb-1">
                            <div className={`h-3 w-3 rounded-full ${dotColorClasses[level]}`} />
                            <span className={`font-medium ${textColorClasses[level]}`}>
                              {info.label}
                            </span>
                          </div>
                          <p className={`text-xs ${descColorClasses[level]}`}>
                            {info.description}
                          </p>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="submit"
              disabled={updateSeverityMutation.isPending || severityQuery.isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {updateSeverityMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Sauvegarder</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AlertRiskLevelsSettings;

